import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Container, FadeIn } from "@/components/Section";
import CinematicImage from "@/components/CinematicImage";

/**
 * PyramidPortal — cinematic atmospheric divider.
 *
 * A wide parallax band built around the TOK pyramid plate. Used as a
 * transition between sections to carry mystery / ancestry / hidden
 * knowledge — the visual mythology behind "awaken the village".
 *
 * Slow scroll-linked parallax + subtle ken-burns drift make the
 * pyramid feel alive rather than static decoration.
 *
 * Props
 *   image      — the pyramid asset (required)
 *   eyebrow    — small kicker line above the headline
 *   line       — the cinematic line (kept short — 1 sentence)
 *   attribution
 *   height     — "tall" | "standard" | "band"  (default "standard")
 *   testId
 */
export default function PyramidPortal({
  image,
  eyebrow,
  line,
  attribution,
  height = "standard",
  testId = "pyramid-portal",
}) {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Slow parallax — the pyramid drifts upward as the section scrolls past.
  const y = useTransform(scrollYProgress, [0, 1], ["8%", "-12%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.12, 1.18]);

  const heights = {
    band: "min-h-[42vh] sm:min-h-[48vh]",
    standard: "min-h-[70vh] sm:min-h-[78vh]",
    tall: "min-h-[88vh]",
  };

  return (
    <section
      ref={ref}
      data-testid={testId}
      className={`relative ${heights[height] || heights.standard} flex items-center overflow-hidden bg-black border-y border-amber-500/10`}
    >
      {/* Parallax plate */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={reduce ? undefined : { y, scale }}
      >
        <CinematicImage
          src={image}
          mobile="50% 40%"
          desktop="center"
          scale={1}
        />
      </motion.div>

      {/* Layered atmosphere — keep the pyramid READABLE while ensuring
          legibility of any overlaid text */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_0%,rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.85)_100%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
      {/* Soft amber bloom from below — molten gold light through the temple floor */}
      <div className="pointer-events-none absolute left-1/2 bottom-0 -translate-x-1/2 h-72 w-[60rem] rounded-full bg-amber-400/[0.08] blur-[140px]" />
      {/* Subtle drifting grain */}
      <div className="tok-dust opacity-50 mix-blend-screen" />

      {(eyebrow || line) && (
        <Container className="relative w-full">
          <FadeIn>
            <div className="max-w-3xl">
              {eyebrow && (
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.45em] text-amber-400 font-bold mb-6">
                  {eyebrow}
                </p>
              )}
              {line && (
                <p className="font-display font-black uppercase italic leading-[1.05] tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                  &ldquo;{line}&rdquo;
                </p>
              )}
              {attribution && (
                <p className="mt-7 text-[11px] uppercase tracking-[0.4em] text-amber-400/90 font-bold">
                  — {attribution}
                </p>
              )}
            </div>
          </FadeIn>
        </Container>
      )}
    </section>
  );
}
